import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { BellIcon } from 'lucide-react';

import analyticsClient, { AnalyticsEvent } from 'src/services/analytics';
import { usePushSubscription } from 'src/services/push-notifications';
import { getPermission } from 'src/services/push-notifications/utils';
import { usePersistentState } from 'src/services/storage/use-persistent-state';
import AlertCard from 'src/components/alerts/AlertCard';

export default function NotificationAlert() {
    const [initialPermissionFetched, setInitialPermissionFetched] = useState(false);
    const [permission, setPermission] = useState<NotificationPermission | null>(null);
    const [isDismissed, setDismissed] = usePersistentState('isNotificationAlertDismissed', false);

    const { createPushSubscription } = usePushSubscription();

    const dismissAlert = () => {
        setDismissed(true);
        analyticsClient.track(AnalyticsEvent.NOTIFICATION_ALERT_DISMISSED);
    };

    const requestPermission = async () => {
        const result = await createPushSubscription();
        if (result) {
            setPermission('granted');
            analyticsClient.track(AnalyticsEvent.NOTIFICATION_REQUEST_GRANTED);
        } else {
            setPermission('denied');
            analyticsClient.track(AnalyticsEvent.NOTIFICATION_REQUEST_DENIED);
        }
    };

    useEffect(() => {
        getPermission()
            .then(setPermission)
            .finally(() => setInitialPermissionFetched(true));
    }, [permission]);

    if (!initialPermissionFetched || isDismissed || permission !== 'default') {
        return null;
    }

    return (
        <AlertCard
            icon={<BellIcon size={22} color="#3b82f6" />}
            title="Enable Desktop Notifications"
            description="You can opt in to receive desktop notifications for new messages."
            borderColor="#3b82f6"
            actions={
                <>
                    <Button variant="outlined" color="inherit" size="small" onClick={requestPermission} sx={{ fontWeight: 600 }}>
                        Enable Notifications
                    </Button>
                    <Button variant="text" color="inherit" size="small" onClick={dismissAlert}>
                        Dismiss
                    </Button>
                </>
            }
        />
    );
}
